import { DocumentTextIcon, OfficeBuildingIcon } from "@heroicons/react/outline";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  requestListBusinessServiceCategories,
  useBusinessServiceDispatch,
  useBusinessServiceState,
} from "../../Context";
import { Alert } from "../../Components/Alert";
import { Button } from "../../Components/Button";
import { useNavigate } from "react-router-dom";
import { EmptyList } from "../../Components/EmptyList";
import MenuItemCard from "../../Components/MenuItemCard";

const BUSINESS_LICENSE_ITEMS = [
  {
    id: "business-establishment",
    titleKey: "businessLicenses.establishmentTitle",
    descriptionKey: "businessLicenses.establishmentDesc",
    icon: OfficeBuildingIcon,
    iconForeground: "text-blue-600",
    iconBackground: "bg-blue-50",
    route: null, // Will be set dynamically based on category data
    isVisible: true,
    isEnabled: true,
    type: "BUSINESS_ESTABLISHMENT",
  },
  {
    id: "deed-amendment",
    titleKey: "businessLicenses.deedAmendmentTitle",
    descriptionKey: "businessLicenses.deedAmendmentDesc",
    icon: DocumentTextIcon,
    iconForeground: "text-teal-600",
    iconBackground: "bg-teal-50",
    route: null, // Will be set dynamically based on category data
    isVisible: true,
    isEnabled: true,
    type: "DEED_AMENDMENT",
  },
];

const BusinessLicenseList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useBusinessServiceDispatch();
  const { businessServiceCategories, loading, error } =
    useBusinessServiceState();

  useEffect(() => {
    requestListBusinessServiceCategories(dispatch, {
      page: 1,
      limit: 10,
      type: ["BUSINESS_ESTABLISHMENT", "DEED_AMENDMENT"],
    });
  }, [dispatch]);

  const services = useMemo(() => {
    return BUSINESS_LICENSE_ITEMS.map((item) => {
      const category = businessServiceCategories.find(
        (cat) => cat.type === item.type
      );
      if (!category) return null;

      return {
        ...item,
        route: `/business-service/${category.id}`,
        title: t(item.titleKey),
        description: t(item.descriptionKey),
      };
    }).filter(Boolean);
  }, [businessServiceCategories, t]);

  const ServiceSkeleton = () => (
    <div className="grid grid-cols-6 gap-4">
      {[...Array(6)].map((_, index) => (
        <div key={index} className="col-span-6 sm:col-span-3 lg:col-span-2">
          <div className="bg-white border border-slate-200/60 rounded-xl p-6 h-full flex flex-col animate-pulse">
            <div className="flex items-start space-x-4 flex-1">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
              </div>
              <div className="flex-1 min-w-0 flex flex-col h-full">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-1"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6 mb-4 flex-1"></div>
                <div className="mt-auto">
                  <div className="w-24 h-10 bg-gray-200 rounded-lg"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return <ServiceSkeleton />;
  }

  if (error) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={error.message}
          errors={error.errors}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-wrap sm:w-full justify-between gap-4">
      <div className="flex flex-col gap-4 justify-around sm:flex-row">
        <div className="w-full justify-start sm:justify-end">
          <Button
            onClick={() => navigate("/business-service/order")}
            title="viewOrderHistory"
          />
        </div>
      </div>
      {services.length > 0 ? (
        <div className="grid grid-cols-6 gap-4">
          {services.map((service) => (
            <MenuItemCard
              key={service.id}
              item={service}
              onClick={() => navigate(service.route)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white col-span-12 rounded-lg p-4">
          <EmptyList title="noServicesAvailable" />
        </div>
      )}
    </div>
  );
};

export default BusinessLicenseList;
